/**
 * useEscapeKey — attach a `keydown` Escape listener while `enabled` is true.
 *
 * Small piece of shared plumbing for hand-rolled dropdowns/popovers that
 * manage their own open state (rather than using MUI's `Menu`, which handles
 * Escape internally). Pass the open flag as `enabled`; the listener is
 * registered on `document` only while open and torn down on close/unmount so
 * we don't leak handlers or catch Escape presses aimed at other components.
 *
 * PENG-13715: the header Domain, Filter, and Time Range dropdowns each own
 * their `isOpen` state and had click-outside dismissal but no Escape
 * handling. Rather than triplicate the same effect, they share this hook.
 */

import { useEffect } from 'react'

// @purpose Fire `onEscape` on Escape keydown while `enabled` is true — for popovers/dropdowns that manage their own open state
export const useEscapeKey = (enabled: boolean, onEscape: () => void): void => {
  useEffect(() => {
    if (!enabled) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onEscape()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [enabled, onEscape])
}
